import {
  useEffect,
  useEffectEvent,
  useId,
  useMemo,
  useRef,
  useState,
  type ClipboardEvent,
  type CSSProperties,
  type KeyboardEvent,
} from 'react'
import { useNavigate } from 'react-router'

import { newCommandId } from '@ctcells/api'
import type { Sample } from '@ctcells/contract'
import { PATHS } from '@ctcells/core'
import { t } from '@ctcells/i18n'

import { useSubmitValidation } from '../../atoms/useSubmitValidation.ts'
import { AddModal } from '../../atoms/AddModal.tsx'
import { Button } from '../../atoms/Button.tsx'
import { cx } from '../../atoms/cx.ts'
import { Combobox } from '../../atoms/Combobox.tsx'
import { Field } from '../../atoms/Field.tsx'
import { DateTimePicker, type DateTimePickerLabels } from '../../atoms/DateTimePicker.tsx'
import { TextInput } from '../../atoms/TextInput.tsx'
import { gate, type ActionGate, type ScreenAccess } from '../access.ts'
import { DISPLAY_TIME_ZONE } from '../format.ts'
import { AddActions, FormHint, FormRow, FormSection, ModalFooter } from '../formSections.tsx'
import { DetailPage, OfflineNotice } from '../resourceParts.tsx'

import {
  addCustomHint,
  hideHint,
  readCustomHints,
  sampleHints,
  sampleTypeHintLabel,
  withCustomHints,
  type HintField,
} from './sampleFormatHints.ts'
import { ExtrasEditor } from './SampleGridCells.tsx'
import { isFutureDay, todayIso } from './sampleModel.ts'
import {
  draftFingerprint,
  duplicateIds,
  EMPTY_FORM,
  formForNext,
  entriesFromGrid,
  entryDraft,
  entryFromForm,
  extrasIssue,
  extrasSummary,
  formDraw,
  formDrawIssue,
  hasMetadata,
  isGridPaste,
  isPendingEntry,
  LIST_COLUMNS,
  parseClipboardGrid,
  parseDraw,
  TAG_JOIN,
  type ListColumn,
  type RowResult,
  type SampleEntry,
  type SampleForm,
} from './sampleRows.ts'

import '../../atoms/table.css'
import '../screens.css'
import '../resource.css'
import './sampleNew.css'

/**
 * 시료 추가 — 와이어프레임 `samples()` `cur.smOpen==='new'` 2434행.
 *
 * 계약(`POST /samples` · `RegisterSampleBody`): `command_id` · `sample_id` 필수,
 * 나머지(`patient_id` · `sample_type` · `rbc_concentration` · `blood_draw_date` ·
 * `cancer_type` · `cancer_stage` · `metadata`)는 선택이다. 제어락 비대상.
 *
 * 🔵 **입력은 위 폼 한 곳, 아래는 담은 목록**(eric 2026-10-04 「시료 번호랑 환자 번호, 타입을 위에서 입력 받고..
 *    밑에 추가 버튼을 누르고.. 아래는.. 빼거나 편집만 보이게 해.. 지금 입력이 여러 곳이 분산되어 있잖아」).
 *    - 폼: 시료 번호* · 환자 번호 · 타입 · 채혈 일시 · 암종 · 암 병기 · 추가 입력. 「저장」(또는 Enter)
 *    - 담으면 **시료 번호 · 환자 번호 · 추가 입력만 비고** 타입 · 채혈 일시 · 암종 · 병기는 남는다 — 종전 「공통 값」 구역을
 *      이것이 대신한다. 포커스는 시료 번호 칸으로
 *    - 목록은 **읽기 전용**이다. 줄마다 [편집](폼으로 올린다 → 「수정 반영」 · 「수정 취소」) · [빼기](한 번 되돌리기)
 *    - 엑셀 여러 줄을 폼에 붙이면 목록에 바로 담긴다(단추 · 붙여 넣을 칸은 걷었다 · eric 2026-10-12). 틀린 줄은 ⚠ + 사유로 서고 [편집] 으로 고친다
 * 🔴 **종전 엑셀식 표는 걷었다** — 칸 안 편집 · 칸 이동 키보드 · 늘 빈 줄 · 줄 칸의 공통 값 흐림 표시 · 「+ 줄 5개」.
 *    되살리지 마라(eric 「입력이 여러 곳이 분산」).
 * 🔴 **적혈구 농도 전용 칸은 없다**(eric 2026-10-04 「적혈구 이건 필수 아니고.. metadata 들어갈 거야」).
 *    `rbc_concentration` 열로 되살리지 마라 — 추가 입력의 예시 이름이다.
 * 🔴 **metadata 는 지금 응답에 안 돌아온다**(BR-59 ② · dev2 `views/samples.py` `_dto` 에 그 키가 없다). 그래서
 *    보내는 줄이 있으면 목록 아래에 그 사실을 적고, 등록된 줄의 결과 칸에도 적는다 — 「보낸 것 = 받은 것」을 보인 척하지 않는다.
 * ⚠ 채혈 «일시»의 시각은 `blood_draw_date`(DATE)에 못 실어 `metadata.blood_draw_at` 으로 보낸다(BR-59 임시).
 *
 * 🔴 **한 줄 = `POST /samples` 한 번**, 차례로 보낸다. 묶음 등록 경로는 계약에 없다 — 지어내지 않는다.
 *    줄마다 결과(등록됨 · 번호 중복 · 그 밖의 실패)를 서버 응답으로만 적는다(낙관적 갱신 없음 · DP-04).
 *    등록된 줄은 잠기고, 남은 줄만 다시 보낸다.
 * 🔴 **이미 등록된 번호는 담지 않는다**(목록 줄이면 ⚠ 로 막는다). 시료는 지우지 않는 원장이라 `GET /samples` 에
 *    있으면 그 번호는 정말 있다 — 막아도 틀리지 않는다. 목록에 없는데 서버에 있으면 409 가 그 줄에 선다.
 * 🔴 **「번호 규칙」 구역은 없다**(eric 2026-10-03). 번호 칸은 빈 채로 시작하고 사람이 적는다.
 * 🔴 **채혈 날짜를 미리 채우지 않는다.** 「오늘」 단추를 눌렀을 때만 넣는다(AI 학습 입력에 지어낸 값이 안 들어가게).
 * ⚠ **환자 ID 재입력 대조를 넣지 않았다.** 서버 계약에 필드가 없고 CLAUDE.md 「아직 미정」 항목이다.
 * ⚠ 브라우저 `datalist` 를 되살리지 마라(eric 2026-09-29 — 고르는 모양은 앱 드롭다운 `Combobox`).
 * ⚠ 창 바닥 주 버튼은 「추가」 그대로다(추가 창 일곱 통일 · eric 2026-09-30) — 건수는 옆 보조 글자.
 *    폼 안의 단추는 「저장」이다(목록에 넣는다 · eric 2026-10-12 — 종전 「목록에 담기」).
 */

/** 한 줄 등록의 결과 — 컨테이너가 서버 응답을 이 셋으로 옮긴다 */
export type RegisterOutcome = { ok: true } | { ok: false; duplicate: boolean; message: string }

export interface SampleNewScreenProps {
  access?: ScreenAccess
  /**
   * 한 줄 등록. 🔴 **화면은 요청만 보낸다** — 결과는 이 약속이 돌려준 서버 응답이다.
   * 없으면(demo) 보내지 않는다.
   */
  onRegister?: ((draft: RegisterDraft, commandId: string) => Promise<RegisterOutcome>) | undefined
  /**
   * 이미 등록된 시료 — **부가 조회**다(「이미 등록된 번호」 판정 · 제안의 재료). 🔴 이것이 없거나 실패해도
   * 등록은 선다 — 판정 없이, 제안은 고정 제안만으로 선다. 마지막 판정은 서버(409)다.
   */
  existing?: readonly Sample[] | undefined
  existingLoading?: boolean | undefined
  /** 부가 조회 실패 사유(403 포함). 그 자리에만 적는다 — 화면을 게이트하지 않는다 */
  existingError?: string | undefined
}

/** 새 작업 화면의 빠른 시료 등록 폼(`NewBatchScreen`)이 쓰는 초안 — 칸 7개 그대로 */
export interface SampleDraft {
  sample_id: string
  patient_id: string
  sample_type: string
  rbc_concentration: string
  blood_draw_date: string
  cancer_type: string
  cancer_stage: string
}

/**
 * 등록 본문 초안(`registerSampleVars` 가 받는다). 시료 추가는 적혈구 농도 칸이 없고 `metadata` 를 싣는다.
 * 🔴 `metadata` 는 등록(POST · 신규)이라 새로 지어 보낸다 — 정정(PUT)과 달리 덮을 옛 값이 없다.
 */
export type RegisterDraft = Omit<SampleDraft, 'rbc_concentration'> & {
  rbc_concentration?: string
  metadata?: Record<string, unknown>
}

const NO_SAMPLES: readonly Sample[] = []

/**
 * 추가된 시료 카드의 칸 차례 — **폼의 보이는 차례**를 따른다(eric 2026-10-13 「환자번호와 타입 순서를 바꿔줘」 → 시료 번호 · 타입 · 환자 번호 …).
 * 🔴 붙여넣기 열 차례(`LIST_COLUMNS` · 엑셀에서 복사해 오는 자료 모양)와 **다르다** — 그쪽은 사용자 자료라 그대로 둔다.
 */
/**
 * 시료 목록 표의 줄 자리 — 담은 것이 적어도 이만큼은 자리를 지킨다(빈 줄 · eric 2026-10-21 「시료 추가하는 것을... 5개 정도 넣을 수 있게..
 * 칸 좀 넓혀줘」). 뒤 창이 시료 정보 팝업을 감쌀 만큼 키를 갖는다. 넘치면 이 줄 수 높이에서 멈추고 안에서 구른다(`sampleNew.css`)
 */
const LIST_SLOT_ROWS = 5
/** 비었을 때 「시료를 추가하세요」가 서는 빈 줄 — 줄 자리 한가운데 */
const EMPTY_MESSAGE_ROW = Math.floor((LIST_SLOT_ROWS - 1) / 2)

const CARD_COLUMNS: readonly ListColumn[] = [
  'sample_id',
  'sample_type',
  'patient_id',
  'blood_draw',
  'cancer_type',
  'cancer_stage',
  'extras',
]

/** 칸 하나의 판정 — 오류(막는다)와 주의(알리기만) */
interface CellIssue {
  kind: 'error' | 'warn'
  text: string
}

/** 목록 줄의 칸별 판정. 등록된 줄은 판정하지 않는다 */
function entryIssues(
  entry: SampleEntry,
  dupIds: ReadonlySet<string>,
  existingIds: ReadonlySet<string>,
  today: string,
): Partial<Record<ListColumn, CellIssue>> {
  if (!isPendingEntry(entry)) return {}
  const out: Partial<Record<ListColumn, CellIssue>> = {}
  const id = entry.sample_id.trim()
  if (id === '') out.sample_id = { kind: 'error', text: t('validation.sampleIdRequired') }
  else if (entry.result?.kind === 'duplicate' && entry.result.sampleId === id)
    out.sample_id = { kind: 'error', text: entry.result.message }
  else if (dupIds.has(id)) out.sample_id = { kind: 'error', text: t('sampleNew.cell.dupInForm') }
  else if (existingIds.has(id)) out.sample_id = { kind: 'error', text: t('sampleNew.row.exists') }
  // 타입은 필수다(eric 2026-10-12 「타입 필수라고 별표」) — 붙여 넣은 줄에 없으면 그 칸이 막는다
  if (entry.sample_type.trim() === '')
    out.sample_type = { kind: 'error', text: t('validation.sampleTypeRequired') }
  const draw = parseDraw(entry.blood_draw)
  if (draw === 'invalid') out.blood_draw = { kind: 'error', text: t('sampleNew.drawAt.format') }
  else if (draw !== null && isFutureDay(draw.date, today))
    out.blood_draw = { kind: 'warn', text: t('sampleNew.drawDate.future') }
  const extras = extrasIssue(entry.extras)
  if (extras !== undefined)
    out.extras = {
      kind: 'error',
      text: t(`sampleNew.extras.${extras.problem}`, { key: extras.key ?? '' }),
    }
  return out
}

export function SampleNewScreen({
  access = {},
  onRegister,
  existing = NO_SAMPLES,
  existingLoading = false,
  existingError,
}: SampleNewScreenProps) {
  const navigate = useNavigate()
  const ids = useId()
  const [form, setForm] = useState<SampleForm>(EMPTY_FORM)
  const [entries, setEntries] = useState<SampleEntry[]>([])
  /** 바로 전에 뺀 줄 — 한 번 되돌릴 수 있다 */
  const [removed, setRemoved] = useState<{ entry: SampleEntry; index: number } | null>(null)
  const [sending, setSending] = useState(false)
  const [pasteNote, setPasteNote] = useState<string | null>(null)
  /** 시료 정보 팝업 — 창을 처음 열면 떠 있다(eric 2026-10-21). 목록 제목 줄 「＋ 시료 추가」가 다시 연다 */
  const [formOpen, setFormOpen] = useState(true)
  const idRef = useRef<HTMLDivElement>(null)
  const focusId = useRef(false)
  const today = todayIso()

  /** 「+ 직접 입력」으로 적은 값 — 이 브라우저에 남아 다음에도 목록 앞에 선다(eric 2026-10-21) */
  const [custom, setCustom] = useState(readCustomHints)
  const hints = useMemo(() => withCustomHints(sampleHints(existing), custom), [existing, custom])
  const remember = (field: HintField) => (value: string) => setCustom(addCustomHint(field, value))
  /** × — 이 브라우저의 목록에서만 뺀다(등록된 시료 · 고정 제안은 숨김 · eric 2026-10-21). 칸의 값은 그대로 */
  const forget = (field: HintField) => (value: string) => setCustom(hideHint(field, value))
  const removeLabel = (value: string) =>
    t('sampleNew.hints.remove', { value: sampleTypeHintLabel(value) })
  const existingIds = useMemo(() => new Set(existing.map((s) => s.sample_id)), [existing])

  // ── 목록 판정 ──
  const pending = entries.filter(isPendingEntry)
  const dupIds = duplicateIds(pending.map((e) => e.sample_id))
  const issues = entries.map((e) => entryIssues(e, dupIds, existingIds, today))
  let errorCount = 0
  for (const byCol of issues)
    for (const issue of Object.values(byCol)) if (issue?.kind === 'error') errorCount += 1

  // ── 폼 판정 ──
  const draw = formDraw(form)
  const futureDraw = draw !== null && draw !== 'invalid' && isFutureDay(draw.date, today)
  const formExtras = extrasIssue(form.extras)
  const v = useSubmitValidation((d: SampleForm) => ({
    sample_id:
      d.sample_id.trim() === ''
        ? t('validation.sampleIdRequired')
        : entries.some((e) => e.sample_id.trim() === d.sample_id.trim())
          ? t('validation.sampleIdInList')
          : existingIds.has(d.sample_id.trim())
            ? t('validation.sampleIdTaken')
            : undefined,
    sample_type: d.sample_type.trim() === '' ? t('validation.sampleTypeRequired') : undefined,
    draw: formDrawIssue(d) !== undefined ? t('validation.drawAtInvalid') : undefined,
    extras: extrasIssue(d.extras) !== undefined ? t('validation.extrasInvalid') : undefined,
  }))
  const listRef = useRef<HTMLDivElement>(null)
  const listValidation = useSubmitValidation((d: { count: number; errors: number }) => ({
    rows:
      d.count === 0
        ? t('validation.sampleRowsRequired')
        : d.errors > 0
          ? t('validation.sampleListErrors')
          : undefined,
  }))
  const putGate: ActionGate = sending
    ? { disabled: true, disabledReason: t('reason.registering') }
    : { disabled: false }
  const createGate: ActionGate = sending
    ? { disabled: true, disabledReason: t('reason.registering') }
    : onRegister === undefined
      ? { disabled: true, disabledReason: t('reason.demoSource') }
      : gate(access, { perm: 'control' })
  const updateListValidation = useEffectEvent(() => {
    listValidation.change('rows', { count: pending.length, errors: errorCount })
  })
  useEffect(() => {
    updateListValidation()
  }, [pending.length, errorCount])

  useEffect(() => {
    if (!focusId.current) return
    focusId.current = false
    idRef.current?.querySelector<HTMLInputElement>('input[name="sample_id"]')?.focus()
  })

  const patchForm = (patch: Partial<SampleForm>) => {
    const next = { ...form, ...patch }
    setForm(next)
    for (const key of Object.keys(patch)) {
      if (key === 'sample_id' || key === 'sample_type' || key === 'extras') v.change(key, next)
      if (key === 'draw_date' || key === 'draw_time') v.change('draw', next)
    }
  }

  /**
   * 「+ 시료 추가 →」. 목록 줄 [편집]은 걷었다(eric 2026-10-20 「편집은 빼줘」) — 틀린 줄은 [빼기] 하고 폼에서 다시 넣는다
   */
  const put = (): boolean => {
    if (putGate.disabled === true || !v.validate(form, idRef.current)) return false
    v.reset()
    setEntries((cur) => [...cur, entryFromForm(form)])
    setForm(formForNext(form))
    setRemoved(null)
    focusId.current = true
    return true
  }
  /** 팝업 [확인](· Enter) — 목록에 한 줄 담고 팝업을 닫는다. 폼은 타입만 남기고 비운다(`formForNext`) — 다음에 열면 그 상태다 */
  const confirmPut = () => {
    if (put()) setFormOpen(false)
  }
  /** 보내는 중에는 팝업을 다시 열지 않는다 — 등록이 도는 동안 목록이 바뀌면 어느 줄이 나갔는지 흐려진다 */
  const sendingGate = { disabled: true, disabledReason: t('reason.registering') } as const

  const removeEntry = (entry: SampleEntry) => {
    const index = entries.findIndex((e) => e.key === entry.key)
    setEntries((cur) => cur.filter((e) => e.key !== entry.key))
    setRemoved({ entry, index })
  }

  const undoRemove = () => {
    if (removed === null) return
    setEntries((cur) => {
      const next = [...cur]
      next.splice(Math.min(removed.index, next.length), 0, removed.entry)
      return next
    })
    setRemoved(null)
  }

  /** 붙여 넣은 여러 줄 → 목록 끝에 담는다 */
  const takePaste = (text: string): boolean => {
    if (!isGridPaste(text)) return false
    const added = entriesFromGrid(parseClipboardGrid(text), LIST_COLUMNS.map(columnLabel))
    setEntries((cur) => [...cur, ...added])
    setPasteNote(t('sampleNew.paste.added', { count: added.length }))
    setRemoved(null)
    /* 붙인 줄이 보이게 팝업을 닫는다(목록이 팝업 뒤에 있다) */
    setFormOpen(false)
    return true
  }

  /** 폼 안 Enter — 칸이 먼저 쓰지 않았으면(칩 만들기 · 제안 고르기 · 다음 항목) 담는다 */
  const onFormKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    /*
     * 🔴 펼친 제안 목록을 닫는 Esc(칸이 `preventDefault` 한 것)는 창까지 가지 않게 한다 — 창(`Modal`)은 문서에서
     *    Esc 를 받아 닫히고, 닫히면 **담은 목록이 통째로 사라진다**(2026-10-04 브라우저 확인 중 실제로 났다)
     */
    if (e.key === 'Escape' && e.defaultPrevented) {
      e.stopPropagation()
      return
    }
    if (e.defaultPrevented || e.key !== 'Enter') return
    if (e.nativeEvent.isComposing || e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) return
    if (!(e.target instanceof HTMLInputElement)) return
    e.preventDefault()
    confirmPut()
  }

  /** 폼 칸에 엑셀 여러 줄을 붙이면 목록에 담는다 — 한 칸짜리는 그 칸의 보통 붙여넣기다 */
  const onFormPaste = (e: ClipboardEvent<HTMLDivElement>) => {
    if (e.defaultPrevented || sending) return
    if (takePaste(e.clipboardData.getData('text/plain'))) e.preventDefault()
  }

  const send = async () => {
    if (createGate.disabled || onRegister === undefined) return
    if (!listValidation.validate({ count: pending.length, errors: errorCount }, listRef.current))
      return
    setSending(true)
    setRemoved(null)
    let allOk = true
    for (const entry of entries) {
      if (!isPendingEntry(entry)) continue
      const draft = entryDraft(entry, DISPLAY_TIME_ZONE)
      const sampleId = draft.sample_id
      const fingerprint = draftFingerprint(draft)
      const commandId =
        entry.sent?.fingerprint === fingerprint ? entry.sent.commandId : newCommandId()
      const outcome = await onRegister(draft, commandId)
      const result: RowResult = outcome.ok
        ? { kind: 'ok', withMetadata: hasMetadata(draft) }
        : outcome.duplicate
          ? { kind: 'duplicate', message: outcome.message, sampleId }
          : { kind: 'failed', message: outcome.message }
      setEntries((cur) =>
        cur.map((e) =>
          e.key === entry.key ? { ...e, sent: { commandId, fingerprint }, result } : e,
        ),
      )
      if (!outcome.ok) {
        allOk = false
        // 번호 중복은 그 줄만의 일이다. 그 밖의 실패(권한 · 연결 · 서버)는 다음 줄도 같다 — 거기서 멈춘다
        if (!outcome.duplicate) break
      }
    }
    setSending(false)
    if (allOk) void navigate(PATHS.samples)
  }

  let idNote: string | undefined
  if (existingError !== undefined)
    idNote = t('sampleNew.existing.unread', { reason: existingError })
  else if (existingLoading) idNote = t('sampleNew.existing.checking')

  /** 목록의 결과 열 — 등록 결과가 생겨야 선다 */
  const showResult = entries.some((e) => e.result !== undefined)

  return (
    <DetailPage
      actions={
        /*
          「추가」 창 넷의 바닥 줄(eric 2026-09-30). 창 안에서는 「추가」 + 옆에 보낼 건수, 페이지에서는 「N건 추가」.
          🔴 보내는 중에 떠나면 어느 줄이 등록됐는지 못 본다 — 취소도 창의 × · Esc 와 같이 막는다
        */
        /*
          🔴 시료 창은 바닥 주 버튼이 「확인」(목록 전체를 등록하는 최종 확인 · 왼쪽 「+ 추가」와 갈린다) · 바닥 건수는 걷었다
             (eric 2026-10-13 「취소 그리고 확인 버튼으로.. 0건은 없애주고」) — 건수는 오른쪽 「추가된 시료」 제목 아래 한 곳이다
        */
        <AddActions
          busy={sending}
          modalLabel={t('sampleNew.confirm')}
          onCancel={() => void navigate(PATHS.samples)}
          onSubmit={() => void send()}
          pageLabel={t('sampleNew.submit', { count: pending.length })}
          submitGate={createGate}
        />
      }
      backLabel={t('screen.samples.title')}
      backTo={PATHS.samples}
      /* 창(`SampleNewModalRoute`) 안이면 보내는 중에 창이 안 닫힌다. 페이지에서는 아무 일도 안 한다 */
      busy={sending}
      title={t('screen.sampleNew.title')}
    >
      <OfflineNotice shown={access.offline === true} />
      {/*
        시료 정보 입력은 팝업(창 위의 창)이고, 창 본문은 «시료 목록» 하나다(eric 2026-10-21 「시료 정보 입력 창이 시료 목록 앞에. 팝업으로 뜨게
        해서.. 시료 정보 쪽.. 취소 확인.. 시료 목록 타이틀 오른쪽에 +시료 추가 버튼」). 처음 열면 팝업이 떠 있다. 팝업 [확인] = 목록에 한 줄
        담기(종전 「시료 추가 →」) — 서버 등록은 여전히 창 바닥 [확인]이다. 팝업 [취소] · Esc · × 는 팝업만 닫는다(`Modal` 열린 창 줄)
      */}
      {formOpen ? (
        <AddModal
          size="md"
          onClose={() => {
            v.reset()
            setFormOpen(false)
          }}
          title={t('sec.sample.input')}
        >
          {/* 구획 제목은 팝업 머리(「시료 정보」)가 말한다 — 같은 글자를 두 번 세우지 않는다 */}
          <FormSection>
            {/* 입력 한 곳 — Enter 는 담기(= [확인]), 엑셀 여러 줄을 붙이면 목록으로 */}
            <div className="snform" onKeyDown={onFormKeyDown} onPaste={onFormPaste} ref={idRef}>
              {/*
              두 칸씩 두 줄(eric 2026-10-12 「2열로 해줘... 두개 두개씩」) — 시료 번호 · 타입 / 환자 번호 · 채혈 일시
              (eric 2026-10-13 「환자번호와 타입 순서를 바꿔줘」 · DOM 차례 = 보이는 차례 = 탭 차례)
            */}
              <FormRow>
                <Field {...v.field('sample_id').fieldProps} label={t('column.sampleId')} required>
                  <TextInput
                    autoComplete="off"
                    {...v.field('sample_id').inputProps}
                    name="sample_id"
                    onChange={(v) => patchForm({ sample_id: v })}
                    readOnly={sending}
                    value={form.sample_id}
                    wide
                  />
                </Field>
                <Field
                  {...v.field('sample_type').fieldProps}
                  label={t('field.sampleType')}
                  required
                >
                  <Combobox
                    {...v.field('sample_type').inputProps}
                    label={t('field.sampleType')}
                    name="sample_type"
                    onChange={(v) => patchForm({ sample_type: v })}
                    onCreate={remember('sampleType')}
                    onRemove={forget('sampleType')}
                    optionLabel={sampleTypeHintLabel}
                    options={hints.sampleType}
                    pickFirst
                    readOnly={sending}
                    create={CREATE_ROW}
                    removeLabel={removeLabel}
                    toggleLabel={t('sampleNew.hints.open', { field: t('field.sampleType') })}
                    value={form.sample_type}
                  />
                </Field>
              </FormRow>
              <FormRow>
                <Field htmlFor={`${ids}-patient`} label={t('column.patientId')}>
                  <TextInput
                    autoComplete="off"
                    id={`${ids}-patient`}
                    name="patient_id"
                    onChange={(v) => patchForm({ patient_id: v })}
                    readOnly={sending}
                    value={form.patient_id}
                    wide
                  />
                </Field>
                <Field {...v.field('draw').fieldProps} label={t('field.drawAt')}>
                  <span className="sndraw">
                    {/*
                     * 날짜 · 시각을 칸 하나로(eric 2026-10-20 「채혈 일시에.. 년월일.. 시분을 하나의 입력 창에서」) — 앱 달력(`DateTimePicker` ·
                     * 「mm/dd/yyyy 이거 없애고.. 달력 누르고 선택하게」). 폼 값은 종전대로 `draw_date` · `draw_time` 둘이다(붙여 넣기 · 검증 · 보내기가
                     * 그대로). 시각은 비울 수 있다 — 모르는 시각을 지어 싣지 않는다
                     */}
                    <DateTimePicker
                      aria-label={t('field.drawAt')}
                      date={form.draw_date}
                      {...v.field('draw').inputProps}
                      labels={dateTimeLabels()}
                      name="draw_at"
                      onChange={(next) => patchForm({ draw_date: next.date, draw_time: next.time })}
                      readOnly={sending}
                      time={form.draw_time}
                      today={today}
                    />
                    {/* 「오늘」 단추는 걷었다(eric 2026-10-20 「채혈 일시에 오늘 버튼은 빼줘」) — 달력에서 오늘이 테로 보인다 */}
                  </span>
                </Field>
              </FormRow>
              <FormRow>
                {(['cancer_type', 'cancer_stage'] as const).map((col) => (
                  <Field htmlFor={`${ids}-${col}`} key={col} label={columnLabel(col)}>
                    {/*
                    🔴 하나만 고른다(eric 2026-10-12 「시료가 하나잖아. 암종.. 하나만 선택 가능하게.. 타입처럼 맨 처음에 직접 적는 선택란」).
                       값 모양(배열 · `metadata.cancer_types`)은 그대로 두고 0 · 1개만 싣는다 — 붙여 넣은 줄에 여럿이 있으면 첫 값만 보인다
                  */}
                    <Combobox
                      create={CREATE_ROW}
                      id={`${ids}-${col}`}
                      label={columnLabel(col)}
                      name={col}
                      onChange={(v) => {
                        const next = v.trim() === '' ? [] : [v]
                        patchForm(
                          col === 'cancer_type' ? { cancer_type: next } : { cancer_stage: next },
                        )
                      }}
                      onCreate={remember(col === 'cancer_type' ? 'cancerType' : 'cancerStage')}
                      onRemove={forget(col === 'cancer_type' ? 'cancerType' : 'cancerStage')}
                      options={col === 'cancer_type' ? hints.cancerType : hints.cancerStage}
                      pickFirst
                      readOnly={sending}
                      removeLabel={removeLabel}
                      toggleLabel={t('sampleNew.hints.open', { field: columnLabel(col) })}
                      value={form[col][0] ?? ''}
                    />
                  </Field>
                ))}
              </FormRow>
              {futureDraw ? <FormHint>{t('sampleNew.drawDate.future')}</FormHint> : null}
              <Field label={t('field.sampleExtras')} {...v.field('extras').fieldProps}>
                <div id={v.field('extras').inputProps.id}>
                  <ExtrasEditor
                    entries={form.extras}
                    error={
                      !v.errors.extras || formExtras === undefined
                        ? undefined
                        : t(`sampleNew.extras.${formExtras.problem}`, { key: formExtras.key ?? '' })
                    }
                    invalidIds={
                      v.errors.extras ? (formExtras?.ids ?? new Set<string>()) : new Set<string>()
                    }
                    onChange={(extras) => patchForm({ extras })}
                    readOnly={sending}
                    title={t('field.sampleExtras')}
                  />
                </div>
              </Field>
              {idNote === undefined ? null : <FormHint role="status">{idNote}</FormHint>}
            </div>
          </FormSection>
          <ModalFooter>
            <AddActions
              busy={false}
              framed
              modalLabel={t('sampleNew.confirm')}
              onCancel={() => {
                v.reset()
                setFormOpen(false)
              }}
              onSubmit={confirmPut}
              pageLabel={t('sampleNew.confirm')}
              submitGate={putGate}
            />
          </ModalFooter>
        </AddModal>
      ) : null}
      <div className="snlist" ref={listRef}>
        <FormSection
          actions={
            <span
              id={listValidation.field('rows').inputProps.id}
              aria-invalid={listValidation.hasErrors || undefined}
              aria-describedby={listValidation.field('rows').inputProps.describedBy}
            >
              <Button
                onClick={() => {
                  v.reset()
                  setFormOpen(true)
                }}
                size="pill"
                {...(sending ? sendingGate : {})}
              >
                {t('sampleNew.list.add')}
              </Button>
            </span>
          }
          step={2}
          title={t('sec.sample.rows')}
          wide
        >
          {/* 「엑셀에서 붙여넣기」 단추 · 붙여 넣을 칸은 걷었다(eric 2026-10-12 「이 버튼 없애줘」) — 폼에 그대로 붙여 넣는 길(`onFormPaste`)은 남는다 */}
          {/* 건수 줄(「N건 · 오류 M」)은 걷었다(eric 2026-10-20 「1건 빼주고」) — 오류는 칸마다 · 확인 단추 사유가 말한다 */}
          {listValidation.errors.rows ? (
            <p className="a-field__error" id={listValidation.field('rows').fieldProps.errorId}>
              {listValidation.errors.rows}
            </p>
          ) : null}
          {pasteNote === null ? null : <FormHint role="status">{pasteNote}</FormHint>}
          {removed === null ? null : (
            <p className="snundo" role="status">
              {t('sampleNew.list.removed', {
                id: removed.entry.sample_id.trim() === '' ? t('value.na') : removed.entry.sample_id,
              })}{' '}
              <Button onClick={undoRemove} size="sm">
                {t('sampleNew.list.undo')}
              </Button>
            </p>
          )}
          {/* 창 폭이 모자라면 목록 표만 가로로 민다 — 창 전체가 넘치지 않게 */}
          <div
            className={cx(
              'sngrid__scroll',
              entries.length > LIST_SLOT_ROWS && 'sngrid__scroll--over',
            )}
            style={{ '--sn-slots': LIST_SLOT_ROWS } as CSSProperties}
          >
            <table className="a-tbl a-tbl--managed sngrid">
              <thead>
                <tr>
                  {CARD_COLUMNS.map((col) => (
                    <th className={`sngrid__h sngrid__h--${col}`} key={col} scope="col">
                      {columnLabel(col)}
                    </th>
                  ))}
                  {/* 결과 열은 결과(등록됨 · 실패 · 수정 중)가 생겨야 선다 — 빈 열이 표 폭을 먹어 가로로 밀리지 않게 */}
                  {showResult ? <th scope="col">{t('sampleNew.col.result')}</th> : null}
                  <th scope="col">
                    <span className="a-sr">{t('sampleNew.col.actions')}</span>
                  </th>
                </tr>
              </thead>
              {/*
                줄 자리 고정 — 담은 줄 뒤에 빈 줄을 채워 늘 `LIST_SLOT_ROWS` 줄 자리를 지킨다(빈 줄은 보조기술에서 숨긴다). 비었으면
                「시료를 추가하세요」가 그 자리 가운데 줄에 한 번 선다(eric 2026-10-20 — 10-13 의 「0건」 줄을 대신한다)
              */}
              <tbody>
                {entries.map((entry, i) => (
                  <EntryRow
                    entry={entry}
                    idBase={`${ids}-r${entry.key}`}
                    issues={issues[i] ?? {}}
                    key={entry.key}
                    n={i + 1}
                    onRemove={() => removeEntry(entry)}
                    sending={sending}
                    showResult={showResult}
                  />
                ))}
                {Array.from({ length: Math.max(0, LIST_SLOT_ROWS - entries.length) }, (_, i) => {
                  const message = entries.length === 0 && i === EMPTY_MESSAGE_ROW
                  return (
                    <tr
                      aria-hidden={message ? undefined : true}
                      className="snrow--slot"
                      key={`slot-${String(i)}`}
                    >
                      <td colSpan={CARD_COLUMNS.length + (showResult ? 1 : 0) + 1}>
                        {message ? (
                          <span className="snlist__empty">{t('sampleNew.list.empty')}</span>
                        ) : null}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
          {/* 「…metadata 로 보냅니다 — 지금 서버가…」 안내 줄은 걷었다(eric 2026-10-13 · 화면에 설명 글을 적지 않는다 · 요청은 BR-59 에 그대로) */}
        </FormSection>
      </div>
      {/* 「작업 배정 — 새 작업에서 배정」 한 줄은 걷었다(eric 2026-10-12 「이 텍스트는 빼고」) */}
    </DetailPage>
  )
}

/** 열 머리글 · 폼 칸 이름 · 붙여넣기 머리글 판정이 같은 낱말을 쓴다 */
/** 고르는 칸(타입 · 암종 · 암 병기) 맨 위 「+ 직접 입력」 줄 */
const CREATE_ROW = {
  empty: t('sampleNew.create.empty'),
  typed: (value: string) => t('sampleNew.create.typed', { value }),
}

function columnLabel(col: ListColumn): string {
  switch (col) {
    case 'sample_id':
      return t('column.sampleId')
    case 'patient_id':
      return t('column.patientId')
    case 'sample_type':
      return t('field.sampleType')
    case 'blood_draw':
      return t('field.drawAt')
    case 'cancer_type':
      return t('field.cancerType')
    case 'cancer_stage':
      return t('field.cancerStage')
    case 'extras':
      return t('field.sampleExtras')
  }
}

/** 목록 칸의 글 — 빈 칸은 흐린 「NA」(서버에는 비운 채 간다) */
function cellText(entry: SampleEntry, col: ListColumn): string {
  switch (col) {
    case 'cancer_type':
    case 'cancer_stage':
      return entry[col].join(TAG_JOIN)
    case 'extras':
      return extrasSummary(entry.extras)
    default:
      return entry[col]
  }
}

function EntryRow({
  entry,
  n,
  idBase,
  issues,
  sending,
  onRemove,
  showResult,
}: {
  entry: SampleEntry
  n: number
  idBase: string
  issues: Partial<Record<ListColumn, CellIssue>>
  sending: boolean
  onRemove: () => void
  showResult: boolean
}) {
  const done = entry.result?.kind === 'ok'
  const bad = Object.values(issues).some((i) => i?.kind === 'error')
  const rowName = entry.sample_id.trim() === '' ? t('sampleNew.row.label', { n }) : entry.sample_id
  let lock: string | undefined
  if (sending) lock = t('reason.registering')
  else if (done) lock = t('reason.sampleRegistered')
  const lockGate: ActionGate =
    lock === undefined ? { disabled: false } : { disabled: true, disabledReason: lock }

  return (
    <tr className={cx('snrow', done && 'snrow--done', bad && 'snrow--bad')} data-row={n - 1}>
      {CARD_COLUMNS.map((col) => {
        const text = cellText(entry, col)
        const issue = issues[col]
        return (
          <td className={`sngrid__c sngrid__c--${col}`} key={col}>
            {text.trim() === '' ? (
              <span className="sngrid__na">{t('value.na')}</span>
            ) : col === 'extras' ? (
              /* 추가 입력이 여럿이면 한 항목씩 아래 줄로(eric 2026-10-20 「추가 입력을 여러 열이면.. 다음 행에 넣어줘」) */
              /* 이름 | 값 짝이 한 줄씩 두 열로 맞선다(eric 2026-10-20 「한 열씩.. 키 값 표현되게.. 따로 노는 느낌」) */
              <dl className="sngrid__extras">
                {entry.extras
                  .filter((e) => e.key.trim() !== '' || e.value.trim() !== '')
                  .map((e) => (
                    <div className="sngrid__extra" key={e.id}>
                      <dt>{e.key.trim()}</dt>
                      <dd>{e.value.trim()}</dd>
                    </div>
                  ))}
              </dl>
            ) : (
              text
            )}
            {issue === undefined ? null : (
              <span
                className={
                  issue.kind === 'error' ? 'a-field__error sngrid__msg' : 'snnote sngrid__msg'
                }
                id={`${idBase}-${col}-msg`}
              >
                {issue.kind === 'error' ? <span aria-hidden="true">⚠ </span> : null}
                {issue.text}
              </span>
            )}
          </td>
        )
      })}
      {showResult ? (
        <td className="snrows__result">
          <RowResultText result={entry.result} />
        </td>
      ) : null}
      {/*
       * 「빼기」 글자 단추는 걷고 작은 × 하나(eric 2026-10-20 「빼기 버튼을 없애줘」) — 줄을 아예 못 빼면 잘못 넣은 줄 · 오류 줄이 남아
       * 「확인」이 막힌 채 창을 닫아야 한다. 접근 이름은 「{시료} 빼기」 그대로
       */}
      <td className="sngrid__act">
        <Button
          aria-label={t('sampleNew.list.removeRow', { name: rowName })}
          onClick={onRemove}
          size="sm"
          {...lockGate}
        >
          <span aria-hidden="true">×</span>
        </Button>
      </td>
    </tr>
  )
}

function RowResultText({ result }: { result: RowResult | undefined }) {
  if (result === undefined) return null
  if (result.kind === 'ok')
    return (
      <span role="status">
        {t('sampleNew.row.ok')}
        {/* 🔴 보낸 metadata 를 서버가 응답에 안 돌려준다(BR-59 ②) — 받은 것을 보인 척하지 않는다 */}
        {result.withMetadata ? (
          <span className="snnote sngrid__msg">{t('sampleNew.row.metadataUnseen')}</span>
        ) : null}
      </span>
    )
  // 번호 중복은 번호 칸에 적었다 — 여기서는 한 낱말로만
  if (result.kind === 'duplicate') return <span>{t('sampleNew.row.duplicate')}</span>
  return (
    <span className="a-field__error" role="alert">
      {result.message}
    </span>
  )
}

/** 달력 글자 — 원자는 한글을 만들지 않는다 */
function dateTimeLabels(): DateTimePickerLabels {
  return {
    placeholder: t('dateTime.placeholder'),
    prevMonth: t('dateTime.prevMonth'),
    nextMonth: t('dateTime.nextMonth'),
    monthTitle: (year, month) =>
      t('dateTime.monthTitle', { year: String(year), month: String(month) }),
    weekdays: t('dateTime.weekdays').split(','),
    hour: t('dateTime.hour'),
    minute: t('dateTime.minute'),
    noTime: t('dateTime.noTime'),
    cancel: t('dateTime.cancel'),
    confirm: t('dateTime.confirm'),
  }
}
